"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ComponentType, createContext, type ReactNode, use, useState, useTransition } from "react";
import { savePageSectionFieldAction, saveProductSectionDataAction } from "@/actions/admin";
import { NULL_STR } from "@/lib/constants";
import { logger } from "@/lib/logger";
import type { Any, IsUnknown } from "@/lib/types";
import { invariant } from "@/lib/utils";
import type { YnsDataType, YnsThemeType } from "@/ui/yns-components/components/types";
import { useStartGlobalTransition } from "@/zustand/admin";
import type { AllPagesPath } from "./built-in-pages";

type NestedPaths<T> = {
	[K in keyof T]: T[K] extends (infer U)[] | undefined | null
		? (K & string) | `${K & string}.${number}` | `${K & string}.${number}.${NestedPaths<NonNullable<U>>}`
		: T[K] extends Record<string, unknown> | undefined | null
			? (K & string) | `${K & string}.${NestedPaths<NonNullable<T[K]>>}`
			: K & string;
}[keyof T];

interface StoreBuilderContextType<TData = Record<string, unknown>, TTheme = Record<string, unknown>> {
	path: string;
	selectedPageSection: null | { idx: number; id: string; pointer: number | null };
	setSelectedPageSection: (args: null | { idx: number; id: string; pointer: number | null }) => void;
	saveSelectedPageSectionThemeField: <TKey extends NestedPaths<NonNullable<TTheme>>>({
		fieldPath,
		value,
	}: {
		fieldPath: TKey;
		value: unknown;
	}) => Promise<void>;
	saveSelectedPageSectionField<TKey extends NestedPaths<NonNullable<TData>>>({
		fieldPath,
		value,
	}: {
		fieldPath: TKey;
		value: unknown;
	}): Promise<void>;
	isPending: boolean;
}

const StoreBuilderContext = createContext<StoreBuilderContextType | null>(null);

export const StoreBuilderContextProvider = ({
	children,
	path,
	productId,
}: {
	children: ReactNode;
	path: AllPagesPath;
	productId?: string;
}) => {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();
	const context = searchParams.get("context");
	const [_preselectedId, preselectedIdx_, preselectedPointer_] = context?.split(".") ?? [];
	const _preselectedIdx = Number(preselectedIdx_);
	const _preselectedPointer = Number(preselectedPointer_);

	// const preselectedPage =
	// 	!Number.isNaN(preselectedIdx) && preselectedId
	// 		? {
	// 				idx: preselectedIdx,
	// 				id: preselectedId,
	// 				pointer: Number.isNaN(preselectedPointer) ? null : preselectedPointer,
	// 			}
	// 		: null;
	const preselectedPage = null;
	console.log(preselectedPage);

	const [selectedPageSection, setSelectedPageSection_] =
		useState<StoreBuilderContextType["selectedPageSection"]>(preselectedPage);
	const [isTransitioning, transition] = useTransition();
	const startGlobalTransition = useStartGlobalTransition();

	// useEffect(() => {
	// 	const context = searchParams.get("context");

	// 	if (selectedPageSection && !context) {
	// 		setSelectedPageSection_(null);
	// 	}
	// }, [searchParams, selectedPageSection]);

	const setSelectedPageSection: StoreBuilderContextType["setSelectedPageSection"] = (arg) => {
		setSelectedPageSection_(arg);

		// support deep link to editing configuration of certain components
		const newSearchParams = new URLSearchParams(searchParams);
		if (arg) {
			newSearchParams.set("context", [arg.id, arg.idx, arg.pointer].join("."));
		} else {
			newSearchParams.delete("context");
		}
		router.push(`${pathname}?${newSearchParams}`, { scroll: false });
	};

	const saveSelectedPageSectionField: StoreBuilderContextType["saveSelectedPageSectionField"] = async ({
		value: value_,
		fieldPath,
	}) => {
		const value = value_ === NULL_STR ? null : value_;

		startGlobalTransition(async () => {
			if (!selectedPageSection) {
				return;
			}

			if (path === "/product/[slug]") {
				if (!productId) {
					// @todo handle this scenario for scope !== 'product'
					logger.error("Product id is missing");
					return;
				}

				transition(async () => {
					// Save field value for selected page section (local - per product)
					await saveProductSectionDataAction({
						pageSectionIdx: selectedPageSection.idx,
						content: value,
						fieldPath,
						path,
						productId,
					});
				});
			} else {
				// Save field value for selected page section (global)
				transition(async () => {
					await savePageSectionFieldAction({
						path,
						pageSectionIdx: selectedPageSection.idx,
						group: "data",
						fieldPath,
						value: JSON.stringify(value),
					});
				});
			}
		});
	};

	const saveSelectedPageSectionThemeField: StoreBuilderContextType["saveSelectedPageSectionThemeField"] =
		async ({ value, fieldPath }) => {
			startGlobalTransition(async () => {
				if (!selectedPageSection) {
					return;
				}

				transition(async () => {
					await savePageSectionFieldAction({
						path,
						pageSectionIdx: selectedPageSection.idx,
						group: "theme",
						fieldPath,
						value: JSON.stringify(value),
					});
				});
			});
		};

	return (
		<StoreBuilderContext
			value={{
				isPending: isTransitioning,
				path,
				selectedPageSection,
				setSelectedPageSection,
				saveSelectedPageSectionThemeField,
				saveSelectedPageSectionField,
			}}
		>
			{children}
		</StoreBuilderContext>
	);
};

export const useStoreBuilderContext = <TComponent extends ComponentType<Any> = ComponentType<Any>>() => {
	const context = use(StoreBuilderContext);

	invariant(context, "useStoreBuilderContext must be used within a StoreBuilderContextProvider");
	type TData = YnsDataType<TComponent>;
	type TTheme = YnsThemeType<TComponent>;
	return context as unknown as StoreBuilderContextType<
		IsUnknown<TData> extends never ? TData : Record<string, unknown>,
		IsUnknown<TTheme> extends never ? TTheme : Record<string, unknown>
	>;
};

export type UseStoreBuilderContextResult<T extends ComponentType<Any>> = ReturnType<
	typeof useStoreBuilderContext<T>
>;
